import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router";
import { cancelSession, getFlow, getSession } from "../apiClient";
import { useSessionStore } from "../stores/sessionStore";
import type { Flow } from "../types/flow";
import { CalendarLegend } from "./calendar/CalendarLegend";
import Footer from "./Footer";
import Header from "./Header";
import { MobileOrderSummaryBar } from "./MobileOrderSummary";
import OrderSummary from "./OrderSummary";
import { AlertDialog } from "./ui/AlertDialog";
import { LoadingSpinner } from "./ui/LoadingSpinner";
import { Message } from "./ui/Message";

type ContextType = { flow: Flow };

export default function Layout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { session, setSession, clearSession } = useSessionStore();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["flow"],
    queryFn: () => getFlow(),
  });

  const [paymentDialogDismissed, setPaymentDialogDismissed] = useState(false);

  // Always fetch fresh session from API when one is stored; poll when checked_out
  const { data: freshSession } = useQuery({
    queryKey: ["session", session?.id],
    queryFn: () => getSession(session!.id),
    enabled: !!session?.id,
    staleTime: 0,
    refetchInterval: session?.status === "checked_out" ? 3000 : false,
  });

  useEffect(() => {
    if (freshSession) {
      if (freshSession.status === "paid") {
        clearSession();
        navigate(`/flow/success?sessionId=${freshSession.id}`);
      } else if (freshSession.status === "cancelled") {
        clearSession();
        navigate("/");
      } else {
        if (freshSession.status !== "checked_out") {
          setPaymentDialogDismissed(false);
        }
        setSession(freshSession);
      }
    }
  }, [freshSession, setSession, clearSession, navigate]);

  const cancelMutation = useMutation({
    mutationFn: () => cancelSession(session!.id),
    onSettled: () => {
      clearSession();
      navigate("/");
    },
  });

  // Countdown timer: seconds remaining until session expires
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!session || session.status !== "pending") {
      setSecondsLeft(null);
      return;
    }

    const update = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(session.expiresAt).getTime() - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setSession({ ...session, status: "expired" });
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [session, setSession]);

  const isCalendarOrItems =
    location.pathname === "/" || location.pathname.startsWith("/items");

  let shouldDialogOpen = false;
  if (session) {
    if (session.status === "checked_out" && !paymentDialogDismissed) {
      shouldDialogOpen = true;
    } else if (session.status === "expired") {
      shouldDialogOpen = true;
    } else if (session.status === "pending" && isCalendarOrItems) {
      shouldDialogOpen = true;
    }
  }

  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!shouldDialogOpen) {
      setDialogOpen(false);
      return;
    }
    const timer = setTimeout(() => setDialogOpen(true), 150);
    return () => clearTimeout(timer);
  }, [shouldDialogOpen]);

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-background text-primary">
        <Header />
        <main className="flex-1 container pt-14 pb-24 flex items-center justify-center">
          <LoadingSpinner size="lg" className="text-blue" />
        </main>
        <Footer />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col min-h-screen bg-background text-primary">
        <Header />
        <main className="flex-1 container pt-14 pb-24 flex items-center justify-center">
          <Message iconName="IconExclamationTriangle" variant="error">
            {t("dialogs.loadError")}
          </Message>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background text-primary">
      <Header />
      <main className="flex-1 container pt-14 pb-24 flex gap-4">
        <div className="w-full sm:w-2/3 flex-1">
          <Outlet context={{ flow: data } satisfies ContextType} />
        </div>
        <div className="hidden sm:block w-1/3 shrink-0 pt-10">
          <OrderSummary />
          {location.pathname === "/" && <CalendarLegend />}
        </div>
      </main>
      <Footer />
      <MobileOrderSummaryBar />

      {session?.status === "pending" && (
        <AlertDialog
          open={dialogOpen}
          title={t("dialogs.existingSession")}
          description={
            <>
              {t("dialogs.existingSessionDescription")}
              {secondsLeft !== null && secondsLeft > 0 && (
                <span className="block mt-1 text-orange font-medium">
                  {t("dialogs.expiresIn", {
                    time: `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`,
                  })}
                </span>
              )}
            </>
          }
        >
          <div className="flex gap-3 justify-end">
            <button className="btn-blue" onClick={() => navigate("/addons/1")}>
              {t("common.continue")}
            </button>
            <button
              className="btn-outline"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              {t("common.startOver")}
            </button>
          </div>
        </AlertDialog>
      )}

      {session?.status === "checked_out" && (
        <AlertDialog
          open={dialogOpen}
          title={t("dialogs.paymentPending")}
          description={
            <span className="flex items-center gap-3">
              <LoadingSpinner size="sm" className="text-blue shrink-0" />
              {t("dialogs.paymentWaiting")}
            </span>
          }
        >
          <div className="flex gap-3 justify-end">
            <button
              className="btn-outline"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              {cancelMutation.isPending ? t("common.loading") : t("dialogs.cancelPayment")}
            </button>
          </div>
        </AlertDialog>
      )}

      {session?.status === "expired" && (
        <AlertDialog
          open={dialogOpen}
          title={t("dialogs.sessionExpired")}
          description={t("dialogs.sessionExpiredDescription")}
        >
          <div className="flex justify-end">
            <button
              className="btn-blue"
              onClick={() => {
                clearSession();
                navigate("/");
              }}
            >
              {t("common.startOver")}
            </button>
          </div>
        </AlertDialog>
      )}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFlow(): ContextType {
  return useOutletContext<ContextType>();
}
